/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import Timeout from "await-timeout";
import * as Yup from "yup";
import { useDebouncedCallback } from "use-debounce";
import { LabSelect } from "../select";
import { handleErrors } from "../../../services/handleErrors";
import useFetcher from "../../../hooks/useFetcher";
import { AsyncLabSelect } from "../../AsyncLabSelect";
import { InputTitle, Label } from "./styles";
import { updateAssociatedGroup, createAssociatedGroup, getAssociatedList, getSubclientesByClientId } from "../../../services/labServices";
import { formatNumeroDeLegajo } from "../segmentationClientGropuModal/clientGroupForm";
import { fetchAllClientes } from "../../../services/clientesServices";

const createOrUpdateAssociatedGroup = async (data, id) => {
  if (id) {
    const { message } = await updateAssociatedGroup(id, data);
    toast.success(message);
    return true;
  }

  const { message } = await createAssociatedGroup(data);
  toast.success(message);
  return true;
};

const promiseOptions = (inputValue, defaultLoad = false) =>
  new Promise((resolve) => {
    setTimeout(async () => {
      const params = {
        query: defaultLoad ? "" : inputValue.query,
        subclienteId: inputValue.subclienteId,
      };

      try {
        const response = await getAssociatedList(params);
        const associatedList = response.result;

        const options =
          associatedList && associatedList.length > 0
            ? associatedList.map((associated) => ({
                label: `${associated.itemId}-${associated.nombre} ${associated.apellido}`,
                value: associated.itemId,
              }))
            : [];

        resolve(options);
      } catch (error) {
        resolve([]);
      }
    }, 500);
  });

const AssociatedGroupForm = ({ toggle, groupInfo, reload }) => {
  const [associatedListDefault, setAssociatedListDefault] = useState([]);
  const [associatedValue, setAssociatedValue] = useState(null);
  const [asociatedIdsList, setAsociatedIdsList] = useState([]);
  const [clientId, setClientId] = useState(groupInfo && groupInfo.labRelsGroupClienteSubclient ? groupInfo.labRelsGroupClienteClient[0].id : 0);
  const [allClientes, isLoadingAllClientes] = useFetcher(fetchAllClientes, undefined, "fetchAllClientes");
  const [subClients, isLoadingSubClients] = useFetcher(getSubclientesByClientId, [clientId]);

  const [clienteSelected, setClienteSelected] = useState(
    groupInfo && groupInfo.labRelsGroupClienteClient
      ? { label: groupInfo.labRelsGroupClienteClient[0].razonSocial, value: groupInfo.labRelsGroupClienteClient[0].id }
      : null
  );
  const [subClientsSelected, setSubClientsSelected] = useState(
    groupInfo && groupInfo.labRelsGroupClienteSubclient
      ? groupInfo.labRelsGroupClienteSubclient.map((e) => ({ label: `${formatNumeroDeLegajo(e.numeroDeLegajo)}`, value: e.itemId }))
      : []
  );

  const [subClientsSelectedString, setSubClientsSelectedString] = useState(null);

  useEffect(() => {
    if (subClientsSelected && subClientsSelected.length > 0) {
      const String = subClientsSelected.map((e) => e.value).join(",");
      setSubClientsSelectedString(String);
    } else {
      setSubClientsSelectedString(null);
    }
  }, [subClientsSelected]);

  //---------------------------------------------------------------------

  const allClientesOptions = allClientes.rows
    ? allClientes.rows.map((razon) => {
        return { label: razon.razonSocial, value: razon.id };
      })
    : [];

  const subClientesOptions =
    subClients && subClients.length > 0
      ? subClients
          .map((e) => {
            return { label: `${formatNumeroDeLegajo(e.numeroDeLegajo)}`, value: e.itemId };
          })
          .sort((a, b) => (a.label > b.label ? 1 : -1))
      : [];

  //---------------------------------------------------------------------

  useEffect(() => {
    if (groupInfo && groupInfo.labRelsGroupAsociado) {
      const associatedList = groupInfo.labRelsGroupAsociado.map((associated) => ({
        label: `${associated.itemId}-${associated.nombre} ${associated.apellido}`,
        value: associated.itemId,
      }));
      setAssociatedValue(associatedList);
    }
  }, [groupInfo]);

  useEffect(() => {
    if (associatedValue && associatedValue.length > 0) {
      associatedValue.map((associated) => {
        setAsociatedIdsList([...asociatedIdsList, associated.value]);
        return null;
      });
    }
  }, [associatedValue]);

  const loadOptions = useDebouncedCallback((inputValue, callback) => {
    const queryInput = inputValue || "";
    const value = { query: queryInput, subclienteId: subClientsSelectedString };
    promiseOptions(value).then(callback);
  }, 500);

  useEffect(() => {
    if (subClientsSelectedString) {
      const value = { query: "", subclienteId: subClientsSelectedString };
      promiseOptions(value, true).then((defaultOptions) => {
        setAssociatedListDefault(defaultOptions);
      });
    }
  }, [subClientsSelectedString]);

  function getFunctionToFilter(filter, selected) {
    if (filter === "associated") {
      setAssociatedValue(selected);
    }

    if (filter === "clientes") {
      setClienteSelected(selected);
      if (selected) {
        setClientId(selected.value);
        setSubClientsSelected([]);
      } else {
        setClientId(0);
        setSubClientsSelected([]);
      }
    }
    if (filter === "subClient") {
      setSubClientsSelected(selected);
    }
  }

  const handleReactSelectChange = (selected, options) => {
    const filtro = options.name;
    getFunctionToFilter(filtro, selected);
  };

  const formik = useFormik({
    initialValues: {
      name: groupInfo ? groupInfo.name : "",
      asociados: [],
    },
    onSubmit: async (values) => {
      const dataToEdit = {
        ...values,
        asociados: asociatedIdsList && asociatedIdsList.length > 0 ? asociatedIdsList : [],
      };

      if (asociatedIdsList && asociatedIdsList.length === 0) {
        toast.error("Debe seleccionar al menos un asociado");
        return;
      }

      try {
        await createOrUpdateAssociatedGroup(dataToEdit, groupInfo ? groupInfo.id : null);
        await Timeout.set(1000);
        toggle();
        reload();
      } catch (error) {
        handleErrors(error, toast.error, null, formik);
      }
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Este campo es requerido."),
    }),
  });

  const AssociatedError = () => {
    const error = { error: false };

    if (asociatedIdsList && asociatedIdsList.length === 0) {
      error.associated = "Debe seleccionar al menos un asociado";
    } else {
      error.associated = null;
    }

    return error;
  };
  const messageError = AssociatedError();

  return (
    <>
      <form className="w-100" onSubmit={formik.handleSubmit}>
        <div className="row w-100" style={{ display: "flex", justifyContent: "center" }}>
          <div className="col-sm-9">
            <div className="form-group">
              <Label htmlFor="name" className="col-form-label pt-0 w-100" style={formik.errors.name && formik.touched.name ? { color: "red" } : null}>
                Nombre del grupo
                <InputTitle
                  name="name"
                  disabled={formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder={formik.errors.name && formik.touched.name ? formik.errors.name : "Nombre del grupo"}
                  style={formik.errors.name && formik.touched.name ? { borderBottom: "1px solid red" } : null}
                />
              </Label>
            </div>
          </div>
          <div className="col-sm-9">
            <div className="form-group">
              <Label htmlFor="clientes" className="col-form-label pt-0 w-100" style={messageError.clientes && formik.touched.clientes ? { color: "red" } : null}>
                Razon Social
                <LabSelect
                  id="clientes"
                  name="clientes"
                  className="w-100"
                  onChange={handleReactSelectChange}
                  value={clienteSelected}
                  placeholder="Seleccionar"
                  isClearable
                  isLoading={isLoadingAllClientes}
                  options={allClientesOptions}
                />
                {messageError.clientes && formik.touched.clientes && <div className="input-feedback">{messageError.clientes}</div>}
              </Label>
            </div>
          </div>
          <div className="col-sm-9">
            <div className="form-group">
              <Label htmlFor="subClient" className="col-form-label pt-0 w-100">
                Sub Cliente
                <LabSelect
                  isMulti
                  id="subClient"
                  name="subClient"
                  className="w-100"
                  onChange={handleReactSelectChange}
                  value={clienteSelected ? subClientsSelected : null}
                  placeholder="Todo"
                  isClearable
                  isLoading={isLoadingSubClients}
                  options={subClientesOptions}
                  disabled={isLoadingSubClients}
                />
              </Label>
            </div>
          </div>
          <div className="col-sm-9">
            <div className="form-group">
              <Label htmlFor="associated" className="col-form-label pt-0 w-100">
                Asociados
                <AsyncLabSelect
                  isMulti
                  id="associated"
                  name="associated"
                  className="w-100"
                  onChange={handleReactSelectChange}
                  value={associatedValue}
                  placeholder="Buscar..."
                  isClearable
                  loadOptions={loadOptions}
                  defaultOptions={subClientsSelectedString ? associatedListDefault : []}
                  isDisabled={!subClientsSelectedString && !(associatedValue && associatedValue.length > 0)}
                />
                {messageError.associated && formik.touched.associated && <div className="input-feedback">{messageError.associated}</div>}
              </Label>
            </div>
          </div>
        </div>
        <div className=" mt-3 mb-0 d-flex justify-content-end">
          <Button color="btn-secondary mr-2" onClick={toggle}>
            Cancelar
          </Button>
          <button className="btn btn-success" disabled={formik.isSubmitting} type="submit">
            Guardar
          </button>
        </div>
      </form>
    </>
  );
};

export default AssociatedGroupForm;
